import type { SignupState } from './types'
import { truthy } from './types'

export interface UnpaidSummary {
  count: number
  total: number
  classCount: number
  extraCount: number
}

/** Count and total of the racer's entered-but-not-yet-paid classes AND added-but-not-yet-paid
 *  extras - what "Continue"/"Go to checkout" would actually charge right now. Paid entries/extras
 *  are excluded from both: they're already settled, so counting them here would overstate what's
 *  still owed. classCount/extraCount are broken out separately (on top of the combined count/total)
 *  so a caller can label the two differently - see unpaidEntryLabel(). */
export function unpaidEntrySummary(signup: SignupState | null): UnpaidSummary {
  if (!signup) return { count: 0, total: 0, classCount: 0, extraCount: 0 }
  let classCount = 0
  let extraCount = 0
  let total = 0
  for (const c of signup.classes) {
    const entered = c.enteredVehicleId !== '' && c.enteredVehicleId !== null && c.enteredVehicleId !== undefined
    if (entered && !truthy(c.paid)) {
      classCount += 1
      total += Number(c.amountDue) || 0
    }
  }
  for (const e of signup.extras) {
    if (truthy(e.added) && !truthy(e.paid)) {
      extraCount += 1
      total += Number(e.amount) || 0
    }
  }
  return { count: classCount + extraCount, total, classCount, extraCount }
}

/** "3 classes" / "1 add-on" / "2 classes, 1 add-on" - singular/plural per type, omitting whichever
 *  type is zero. Empty string when nothing's selected (callers decide whether to render anything
 *  then, and what to append - RegistrationCard adds " selected", ClassesFooterBar uses it bare). */
export function unpaidEntryLabel({ classCount, extraCount }: Pick<UnpaidSummary, 'classCount' | 'extraCount'>): string {
  const parts: string[] = []
  if (classCount > 0) parts.push(`${classCount} class${classCount === 1 ? '' : 'es'}`)
  if (extraCount > 0) parts.push(`${extraCount} add-on${extraCount === 1 ? '' : 's'}`)
  return parts.join(', ')
}

export interface EntryStatusSummary {
  /** Every entered class / added extra, regardless of payment status - unlike UnpaidSummary's
   *  classCount/extraCount above, which deliberately count only the unpaid ones. */
  classCount: number
  extraCount: number
  paidCount: number
  paidTotal: number
  unpaidCount: number
  unpaidTotal: number
}

/** Full paid/unpaid breakdown of everything the racer has entered/added - powers the "You're
 *  entered in N classes" summary card at the top of the mobile Classes list
 *  (MobileClasses.dc.html's own top-of-list card), which needs both halves of the picture at once
 *  rather than unpaidEntrySummary()'s single "what's left to pay" number. A separate pass over
 *  signup rather than building on unpaidEntrySummary - the two answer different questions ("what
 *  would checkout charge" vs "what's my status overall") and signup.classes/extras are always a
 *  handful of rows, so a second small loop costs nothing and keeps each function's contract
 *  simple. */
export function entryStatusSummary(signup: SignupState | null): EntryStatusSummary {
  const result: EntryStatusSummary = { classCount: 0, extraCount: 0, paidCount: 0, paidTotal: 0, unpaidCount: 0, unpaidTotal: 0 }
  if (!signup) return result
  for (const c of signup.classes) {
    const entered = c.enteredVehicleId !== '' && c.enteredVehicleId !== null && c.enteredVehicleId !== undefined
    if (!entered) continue
    result.classCount += 1
    if (truthy(c.paid)) {
      result.paidCount += 1
      result.paidTotal += Number(c.amountDue) || 0
    } else {
      result.unpaidCount += 1
      result.unpaidTotal += Number(c.amountDue) || 0
    }
  }
  for (const e of signup.extras) {
    if (!truthy(e.added)) continue
    result.extraCount += 1
    if (truthy(e.paid)) {
      result.paidCount += 1
      result.paidTotal += Number(e.amount) || 0
    } else {
      result.unpaidCount += 1
      result.unpaidTotal += Number(e.amount) || 0
    }
  }
  return result
}
